import { ATTRIBUTES } from '../constants/attributes';
import {
  SURVEY_TYPES,
  QUESTIONNAIRE_QUESTION_ANSWER_TYPES,
  QUESTIONNAIRE_QUESTION_TYPES,
} from '../constants/survey';
import { FIELD_TYPES } from '../constants/fieldTypes';
import { ATTRIBUTE_TYPES_MAP } from '../components/pages/attributesPage/config';
import {
  isArray,
  isArrayEmpty,
  isValueInList,
  reorderItemsInArrayByIds,
} from './helpers';
import { getPersonFullName, getQuestionText } from './uiUtils';
import { isEmpty, validateLength, isRichTextEditorEmpty } from './validation';

export const HORIZONTAL_ANSWER_SIZES = {
  SMALL: 5,
  MEDIUM: 10,
  LARGE: 16,
};

const isSinglePersonSurvey = survey =>
  survey &&
  ((survey.subjects && survey.subjects.length === 1) ||
    survey.type === SURVEY_TYPES.SELF ||
    survey.survey_type === SURVEY_TYPES.SELF);

export const prepareSurveyData = survey => {
  const { attributes, questionnaire } = survey;
  const isSingleSurvey = isSinglePersonSurvey(survey);
  const pulseTypes = [SURVEY_TYPES.PULSE, SURVEY_TYPES.ENPS, SURVEY_TYPES.NPS];
  const isPulseType =
    pulseTypes.includes(survey.type) || pulseTypes.includes(survey.survey_type);
  const person = isPulseType
    ? { first_name: survey?.subjects?.[0]?.first_name, last_name: '' }
    : survey?.subjects?.[0];
  const title = isSingleSurvey ? getPersonFullName(person) : null;
  let totalQuestions = questionnaire ? questionnaire.question_count : 0;
  if (!questionnaire) {
    attributes.forEach(attribute => {
      totalQuestions += attribute.question_count || attribute.total_questions;
      if (
        survey.type === SURVEY_TYPES.ENPS ||
        survey.survey_type === SURVEY_TYPES.ENPS ||
        attribute?.code === SURVEY_TYPES.ENPS
      ) {
        attribute.name = 'eNPS';
      }
      if (
        survey.type === SURVEY_TYPES.NPS ||
        survey.survey_type === SURVEY_TYPES.NPS
      ) {
        attribute.name = 'NPS';
      }
    });
  }

  const totalReviews = questionnaire ? 1 : attributes.length;

  return {
    ...survey,
    title,
    attributes,
    totalQuestions,
    isSingleSurvey,
    isAnonymous: survey.is_anonymous,
    totalReviews,
    surveyTitle: survey?.title,
  };
};

export const prepareAttributeReviewData = review => {
  const isEnps = review.type === ATTRIBUTE_TYPES_MAP.ENPS.id;
  const hasAdditionalFeedback =
    review[ATTRIBUTES.fields.withAdditionalFeedback];
  const withTextReplyQuestions =
    review?.[ATTRIBUTES.fields.withTextReplyQuestions];
  const results = review.subjects.reduce((acc, subject) => {
    const subjectsAnswers = withTextReplyQuestions
      ? subject.replied
      : subject.answered;

    return {
      ...acc,
      [subject.review_tracking]: {
        ...(isEnps
          ? { ...subject.answered, ...subject.replied }
          : subjectsAnswers),
      },
    };
  }, {});
  const comments = review.subjects.reduce((acc, subject) => {
    return [
      ...acc,
      {
        review_tracking: subject.review_tracking,
        comment: subject.comment || '',
      },
    ];
  }, []);

  const questions = (
    isEnps
      ? review.questions?.map(question => ({
          ...question,
          text: getQuestionText(question.text, review.subjects[0]?.first_name),
        }))
      : review.questions
  )?.map(question =>
    question.text_reply_questions
      ? { ...question, answers: [], answer_count: 0 }
      : question
  );

  return {
    ...review,
    questions,
    results,
    comments,
    isEnps,
    isVertical: review.layout === ATTRIBUTES.verticalLayout,
    hasAnswerComment: review?.has_answer_comment,
    hasNeutralAnswer: review[ATTRIBUTES.fields.withNeutralAnswer],
    hasAdditionalFeedback,
    withTextReplyQuestions,
    totalSteps:
      review.questions.length +
      (hasAdditionalFeedback ? 1 : 0) +
      (isRichTextEditorEmpty(review.visible_description) ? 1 : 0),
    isSubmitted: !review.active,
  };
};

export const prepareQuestionnaireReviewData = review => {
  return {
    ...review,
    questions: review.questions || [],
    results: review.subjects.reduce((acc, subject) => {
      return {
        ...acc,
        [subject.review_tracking]: subject.responses,
      };
    }, {}),
    totalSteps: review.questions.length + (review?.description ? 1 : 0),
    isSubmitted: !review.active,
  };
};
export const prepareAnswerTextResult = answerData => ({
  review_tracking: answerData.review_tracking,
  question: answerData.question,
  reply: answerData.answer.reply,
  not_applicable: answerData.answer.not_applicable,
});

export const prepareAnswerCommentResult = answerData => ({
  review_tracking: answerData.review_tracking,
  question: answerData.question,
  reply: answerData?.comment?.answerComment,
  not_applicable: false,
});

const getUnansweredQuestion = review => {
  const { questions, subjects } = review;
  const withTextReplyQuestions =
    review?.[ATTRIBUTES.fields.withTextReplyQuestions];

  return questions.find(question =>
    subjects.some(subject => {
      const answered = Object.keys(
        withTextReplyQuestions ? subject.replied : subject.answered
      );

      return answered.indexOf(question.id.toString()) === -1;
    })
  );
};

const getEnpsUnansweredQuestions = review => {
  const { questions, subjects } = review;

  return questions.find(question =>
    subjects.some(subject => {
      const answered = Object.keys(
        question.answers.length ? subject.answered : subject.replied
      );

      return answered.indexOf(question.id.toString()) === -1;
    })
  );
};

export const getRankingOptionIds = (options, rankedOptionIds) => {
  const currentOptions = options || [];

  if (isArray(rankedOptionIds) && !isArrayEmpty(rankedOptionIds)) {
    return rankedOptionIds;
  }

  return currentOptions.map(option => option.id);
};

export const getOrderedRankingOptions = (options, rankedOptionIds) => {
  const currentOptions = options || [];

  if (!isArray(rankedOptionIds) || isArrayEmpty(rankedOptionIds)) {
    return currentOptions;
  }

  const orderedOptions = reorderItemsInArrayByIds(
    currentOptions,
    rankedOptionIds
  ).filter(Boolean);
  const orderedIds = orderedOptions.map(option => option.id);
  const missingOptions = currentOptions.filter(
    option => !isValueInList(orderedIds, option.id)
  );

  return [...orderedOptions, ...missingOptions];
};

export const isQuestionCompleted = (
  results,
  subjects,
  questionId,
  questionType
) => {
  if (questionType === QUESTIONNAIRE_QUESTION_TYPES.RANKING) {
    return true;
  }

  return subjects?.every(subject => {
    const result = results[subject.review_tracking]?.[questionId];

    if (!result) {
      return false;
    }

    const answerKey = QUESTIONNAIRE_QUESTION_ANSWER_TYPES[questionType];

    if (!answerKey) {
      return Boolean(result);
    }

    const value = result[answerKey];

    if (isArray(value)) {
      return !isArrayEmpty(value);
    }

    return Boolean(value);
  });
};

export const isAttributeQuestionAnswerSelectedCheck = (
  results,
  answerId,
  user
) => {
  const userResults = results?.[user?.review_tracking];
  return (
    userResults &&
    Object.values(results?.[user.review_tracking]).includes(answerId)
  );
};

export const isQuestionnaireQuestionOptionSelectedCheck = (
  selection,
  questionId,
  optionId,
  subject
) => {
  const userSelection =
    selection?.[subject?.review_tracking]?.[questionId]?.selected_option_ids ??
    [];
  const hasResponse = !isArrayEmpty(userSelection);
  return hasResponse && isValueInList(userSelection, optionId);
};

export const shouldRenderQuestionnaireQuestionOptionSubject = (
  questionId,
  type,
  optionId,
  selection,
  user
) => {
  if (type === QUESTIONNAIRE_QUESTION_TYPES.MULTI_SELECT) {
    return true;
  }

  const selectedValue =
    selection?.[user.review_tracking]?.[questionId]?.[
      QUESTIONNAIRE_QUESTION_ANSWER_TYPES[type]
    ];
  const hasResponse = isArray(selectedValue) && !isArrayEmpty(selectedValue);

  return !hasResponse || isValueInList(selectedValue, optionId);
};

export const shouldRenderAttributeQuestionAnswerSubject = (
  questionId,
  answerId,
  results,
  user
) => {
  const selectedValue = results?.[user.review_tracking]?.[questionId];

  return !selectedValue || selectedValue === answerId;
};

export const resumeAttributeReview = (reviews, review) => {
  const isEnps = review.type === ATTRIBUTE_TYPES_MAP.ENPS.id;
  if (!isArrayEmpty(reviews)) {
    let reviewStep = 0;
    const surveyStep = reviews.findIndex(
      currReview => currReview.id === review.id
    );
    const unansweredQuestion = isEnps
      ? getEnpsUnansweredQuestions(review)
      : getUnansweredQuestion(review);

    if (unansweredQuestion) {
      reviewStep = review.questions.indexOf(unansweredQuestion);
    }
    return {
      surveyStep,
      reviewStep,
    };
  }

  return {
    surveyStep: 0,
    reviewStep: 0,
  };
};

export const hasErrors = errors => Object.values(errors).some(error => !!error);

export const updateScrollPosition = (
  scrollContainer,
  isVertical = true,
  selectedAnswerClass = 'selected'
) => {
  if (!scrollContainer) return;

  const containerBox = scrollContainer.getBoundingClientRect();
  const selectedAnswer = scrollContainer.querySelector(
    `[class*=${selectedAnswerClass}]`
  );

  if (selectedAnswer) {
    const answerBox = selectedAnswer.getBoundingClientRect();
    const containerTop = isVertical ? containerBox.top : 0;

    const top =
      scrollContainer.scrollTop +
      answerBox.top +
      answerBox.height / 2 -
      containerTop -
      containerBox.height / 2;
    const left =
      scrollContainer.scrollLeft +
      answerBox.left +
      answerBox.width / 2 -
      containerBox.left -
      containerBox.width / 2;

    return scrollContainer.scrollTo({
      top: !isVertical || top < 0 ? 0 : top,
      left: isVertical || left < 0 ? 0 : left,
      behavior: 'smooth',
    });
  }

  return scrollContainer.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });
};

export const getActiveReviewIndex = (reviews = []) =>
  reviews.findIndex(review => review.active);

export const getAnswerCommentFields = isDisabled => [
  {
    name: 'answerComment',
    translationKey: 'answerComment',
    type: FIELD_TYPES.RICH_TEXT_EDITOR,
    required: true,
    isReadOnly: isDisabled,
    validators: [
      { type: 'required', validator: isEmpty },
      {
        type: 'answerCommentMaxLength',
        validator: value => validateLength(value, 0, 20000),
      },
    ],
  },
];

export const getCommentInitialData = answerComment => {
  return {
    answerComment: answerComment?.reply || '',
  };
};
